import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type { Hex } from 'viem';

import { Chart, type Level } from '../components/Chart.tsx';
import { ErrorBoundary } from '../components/ErrorBoundary.tsx';
import { HistoryTable, OrdersTable, PositionsTable, StatusList, takeProfitOf } from '../components/Tables.tsx';
import { CoinIcon, compactNum, compactUsd, Row, Tabs, tone } from '../components/ui.tsx';
import { config, MARKETS } from '../config.ts';
import { bustPrice } from '../../../offchain/src/math.ts';
import { amountInput, fmtPx, fmtSignedUsd, fmtUnits, fmtUsd, movePct, netPnl, parseUnits, pct, toInput, WAD } from '../lib/format.ts';
import { DEFAULT_TIMEFRAME, hlEnabled, TIMEFRAMES, type Timeframe } from '../lib/hl.ts';
import { useTicks } from '../lib/ticks.ts';
import { getSlippageBps, priceLimit, setSlippageBps, SLIPPAGE_CHOICES } from '../lib/slippage.ts';
import {
  CancelledError,
  cancelIntent,
  deadlineIn,
  INTENT_TTL_SEC,
  LIMIT_TTLS,
  newNonce,
  run,
  settle,
  submit as submitIntent,
  UnsettledError,
  type Signer,
} from '../lib/intents.ts';
import type { Intent } from '../../../offchain/src/eip712.ts';
import { viewPosition } from '../lib/positions.ts';
import { fillPx, quotePapurr, waitSec } from '../lib/protocol.ts';
import { enableSession } from '../lib/session.ts';
import { errorMessage } from '../lib/tx.ts';
import { useApp } from '../state.tsx';

/** The wallet refused the one-click prompt once this visit: orders are signed by the wallet from then on */
let sessionDeclined = false;

export function TradePage() {
  const { route, markets, positions, globals, account, intents } = useApp();
  const [timeframe, setTimeframe] = useState<Timeframe>(loadTimeframe);
  const [tab, setTab] = useState<'positions' | 'orders' | 'history' | 'status'>('positions');
  const orders = (intents ?? []).filter((i) => i.limit && i.status === 'waiting');
  const m = markets?.find((x) => x.id === route.market);
  const scale = m?.inst.priceScale ?? 10;
  const { ticks, version } = useTicks(route.market, m?.inst.priceScale);

  // entry pills carry the live PnL, bust pills the move left before the position is liquidated; resting
  // limit orders show where they will open
  const levels: Level[] = useMemo(() => {
    if (!m) return [];
    const resting: Level[] = (intents ?? [])
      .filter((i) => i.limit?.kind === 'open' && i.status === 'waiting' && i.limit.instrumentId === m.id)
      .map((i) => ({
        price: Number(BigInt(i.limit!.px)) / scale,
        label: `Limit ${i.limit!.isLong ? 'Long' : 'Short'} ${i.limit!.leverage}×`,
        kind: 'order' as const,
        isLong: i.limit!.isLong,
      }));
    return resting.concat((positions ?? [])
      .filter((p) => p.instrumentId === m.id)
      .flatMap((p) => {
        const v = viewPosition(p, markets, globals);
        const bust = v.bust ?? bustPrice(p.entryPx, p.leverage, p.isLong, m.inst.bustBuffer);
        const side = `${p.isLong ? 'Long' : 'Short'} ${p.leverage}×`;
        const away = m.mid ? Math.abs(movePct(m.mid, bust)) : undefined;
        const tp = takeProfitOf(intents, p.id);
        const tpPx = tp ? BigInt(tp.limit!.px) : undefined;
        const tpNet = tpPx && globals ? netPnl({ entryPx: p.entryPx, exitPx: tpPx, margin: p.margin, leverage: p.leverage, isLong: p.isLong }, m.inst, globals.winFeeRate) : undefined;
        const tpLevel: Level[] = tpPx
          ? [{ price: Number(tpPx) / scale, label: tpNet !== undefined ? `${side} TP · +$${fmtUsd(tpNet)}` : `${side} TP`, kind: 'order', isLong: p.isLong }]
          : [];
        return [
          ...tpLevel,
          { price: Number(p.entryPx) / scale, label: v.net !== undefined ? `${side} · ${v.net < 0n ? '−' : '+'}$${fmtUsd(v.net < 0n ? -v.net : v.net)}` : side, kind: 'entry' as const, isLong: p.isLong },
          { price: Number(bust) / scale, label: away !== undefined ? `${side} bust · ${away.toFixed(2)}% away` : `${side} bust`, kind: 'bust' as const, isLong: p.isLong },
        ];
      }));
  }, [positions, intents, m, markets, globals, scale]);

  return (
    <div className="trade">
      <section className="panel chart-panel">
        <MarketBar timeframe={timeframe} setTimeframe={(t) => setTimeframe(saveTimeframe(t))} />
        {/* the chart draws live data on a canvas: if it throws, the order form and the tables stay usable */}
        <ErrorBoundary area="chart">
          <Chart
            market={route.market}
            timeframe={timeframe}
            ticks={ticks}
            version={version}
            live={m?.mid ? Number(m.mid) / scale : undefined}
            scale={scale}
            levels={levels}
          />
        </ErrorBoundary>
      </section>
      <aside className="side">
        <AccountEquity />
        <OrderForm />
        <SessionRow />
      </aside>
      <section className="panel positions-panel">
        <Tabs
          tabs={[
            { id: 'positions', label: 'Positions', count: positions?.length },
            { id: 'orders', label: 'Orders', count: orders.length || undefined },
            { id: 'history', label: 'History' },
            { id: 'status', label: 'Status', count: intents?.filter((i) => i.status === 'pending' || i.status === 'submitted').length },
          ]}
          value={tab}
          onChange={setTab}
        />
        {tab === 'positions' ? (
          <PositionsTable />
        ) : tab === 'orders' ? (
          <OrdersTable />
        ) : tab === 'history' ? (
          <HistoryTable rows={account?.history ?? []} />
        ) : (
          <StatusList />
        )}
      </section>
    </div>
  );
}

const TF_KEY = 'papurr:timeframe';

function loadTimeframe(): Timeframe {
  try {
    const v = localStorage.getItem(TF_KEY) as Timeframe | null;
    return v && TIMEFRAMES.includes(v) ? v : DEFAULT_TIMEFRAME;
  } catch {
    return DEFAULT_TIMEFRAME;
  }
}

function saveTimeframe(t: Timeframe): Timeframe {
  try {
    localStorage.setItem(TF_KEY, t);
  } catch {
    // storage blocked: the choice lasts for this visit
  }
  return t;
}

function MarketBar({ timeframe, setTimeframe }: { timeframe: Timeframe; setTimeframe: (t: Timeframe) => void }) {
  const { route, go, markets, mids, ctxs, stale } = useApp();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const m = markets?.find((x) => x.id === route.market);
  const meta = MARKETS[route.market];
  const ctx = meta && ctxs?.[meta.hlCoin];
  const scale = m?.inst.priceScale ?? 10;
  const change = (id: number) => {
    const mk = markets?.find((x) => x.id === id);
    const c = MARKETS[id] && ctxs?.[MARKETS[id]!.hlCoin];
    if (!mk?.mid) return undefined;
    if (c?.prevDayPx) return ((Number(mk.mid) / mk.inst.priceScale - c.prevDayPx) / c.prevDayPx) * 100;
    const first = mids[id]?.[0];
    return first ? movePct(first.px, mk.mid) : undefined;
  };
  const chg = change(route.market);
  const oiUsd = (base: bigint) => (m?.mid ? (base * m.mid) / BigInt(scale) : 0n);

  return (
    <div className="market-bar">
      <div className="menu-anchor" ref={ref}>
        <button className="market-pill" onClick={() => setOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={open}>
          <CoinIcon market={route.market} />
          <span className="market-sym">{meta?.symbol}</span>
          <span className="market-px num">${m?.mid ? fmtPx(m.mid, scale) : '—'}</span>
          {chg !== undefined && <span className={`num small ${chg >= 0 ? 'pos' : 'neg'}`}>{`${chg >= 0 ? '+' : ''}${chg.toFixed(2)}%`}</span>}
          <span className="chev">▾</span>
        </button>
        {open && (
          <div className="menu market-menu" role="listbox">
            {markets?.map((x) => {
              const c = change(x.id);
              return (
                <button
                  key={x.id}
                  role="option"
                  aria-selected={x.id === route.market}
                  onClick={() => {
                    go({ market: x.id });
                    setOpen(false);
                  }}
                >
                  <CoinIcon market={x.id} size={18} />
                  <span className="market-sym">{MARKETS[x.id]?.symbol}</span>
                  <span className="grow" />
                  <span className="num">${x.mid ? fmtPx(x.mid, x.inst.priceScale) : '—'}</span>
                  {c !== undefined && <span className={`num small ${c >= 0 ? 'pos' : 'neg'}`}>{`${c >= 0 ? '+' : ''}${c.toFixed(2)}%`}</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="stat-strip">
        {hlEnabled && (
          <span>
            <em>Vol 24h</em> {ctx ? compactNum(ctx.dayNtlVlm) : '—'}
          </span>
        )}
        <span>
          <em>Long OI</em> {m ? compactUsd(oiUsd(m.oi.long)) : '—'}
        </span>
        <span>
          <em>Short OI</em> {m ? compactUsd(oiUsd(m.oi.short)) : '—'}
        </span>
        <select value={timeframe} onChange={(e) => setTimeframe(e.target.value as Timeframe)} aria-label="Chart timeframe">
          {TIMEFRAMES.map((t) => (
            <option key={t} value={t}>
              {t === 'tick' ? 'Tick' : t}
            </option>
          ))}
        </select>
        <span
          className={`live ${m?.midError || stale ? 'is-off' : ''}`}
          title={stale ? 'No new block from the server for a while: this price is old' : m?.midError ? m.midError : 'Updated every block'}
        >
          {m?.midError ? 'NO QUOTE' : stale ? 'STALE' : 'LIVE'}
        </span>
      </div>
    </div>
  );
}

function AccountEquity() {
  const { user, positions, markets, globals } = useApp();
  const [open, setOpen] = useState(true);
  const views = (positions ?? []).map((p) => viewPosition(p, markets, globals));
  const inUse = views.reduce((a, v) => a + v.p.margin, 0n);
  const pnl = views.reduce((a, v) => a + (v.net ?? 0n), 0n);
  const equity = user ? user.balance + inUse + pnl : undefined;
  return (
    <div className="equity">
      <button className="equity-head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="chev small">{open ? '▾' : '▸'}</span> Account Equity
        <span className="grow" />
        <b className="num">{equity !== undefined ? compactUsd(equity) : '- -'}</b>
      </button>
      {open && (
        <div className="equity-body">
          <Row label="Available to Trade">{user ? `$${fmtUsd(user.balance)}` : '- -'}</Row>
          <Row label="Margin in Use">{user ? compactUsd(inUse) : '- -'}</Row>
          <Row label="Open PnL" tone={tone(pnl)}>
            {user && views.length ? compactUsd(pnl) : '- -'}
          </Row>
        </div>
      )}
    </div>
  );
}

function OrderForm() {
  const { route, markets, globals, user, wallet, signer, session, setSession, openConnect, openFunds, toast, refresh } = useApp();
  const [isLong, setLong] = useState(true);
  // market fills now at the band; limit rests with the relayer until the price reaches it
  const [kind, setKind] = useState<'market' | 'limit'>('market');
  const [pxText, setPxText] = useState('');
  const [ttl, setTtl] = useState<number>(LIMIT_TTLS[1].sec);
  const [marginText, setMarginText] = useState('');
  const [levText, setLevText] = useState('50');
  const [busy, setBusy] = useState<string>();
  // the order while it waits in the relayer queue, for the wait notice and its Cancel button
  const [queued, setQueued] = useState<{ id: Hex; ahead: number }>();
  const [slippage, setSlippage] = useState(getSlippageBps);

  const m = markets?.find((x) => x.id === route.market);
  const meta = MARKETS[route.market];
  // the market's real limit; a preview build can show a higher one, but never sends past the real one
  const marketMaxLev = m?.inst.maxLeverage ?? 1000;
  const maxLev = config.previewMaxLeverage > marketMaxLev ? config.previewMaxLeverage : marketMaxLev;
  const lev = Math.min(Math.max(1, Math.floor(Number(levText) || 1)), maxLev);
  const margin = parseUnits(marginText || '0', 18);
  const notional = margin !== undefined ? margin * BigInt(lev) : 0n;
  // a long buys at the top of the band (the worse of the book mid and the mark), a short sells at the bottom
  const fill = fillPx(m, isLong);
  const pxDecimals = Math.round(Math.log10(m?.inst.priceScale ?? 10));
  const limitPx = kind === 'limit' ? parseUnits(pxText, pxDecimals) : undefined;
  // a limit order opens at its limit or better: size, bust and PnL are shown at the limit
  const entry = kind === 'limit' ? limitPx || undefined : fill;
  // a limit already at or past the price fills at once, at the current (better) price
  const fillsNow = kind === 'limit' && !!fill && !!limitPx && (isLong ? fill <= limitPx : fill >= limitPx);
  const limitMove = kind === 'limit' && fill && limitPx ? movePct(fill, limitPx) : undefined;
  // a limit price can be typed before the market is known (relayer slow or down): no figures until it is
  const bust = entry && m ? bustPrice(entry, BigInt(lev), isLong, m.inst.bustBuffer) : undefined;
  const winAt1 =
    entry && m && globals && margin
      ? netPnl({ entryPx: entry, exitPx: (entry * (isLong ? 101n : 99n)) / 100n, margin, leverage: BigInt(lev), isLong }, m.inst, globals.winFeeRate)
      : undefined;

  // PAPURR a liquidation would mint now (on the whole margin), from the tokenomics state in the snapshot
  const papurrIfLiquidated = margin && margin > 0n && globals ? quotePapurr(margin, globals) : undefined;

  let block: string | undefined;
  if (!m || !globals) block = 'Loading…';
  else if (globals.openPaused || m.inst.paused || !m.inst.active) block = 'Market Paused';
  else if (kind === 'limit' && !pxText) block = 'Enter Price';
  else if (kind === 'limit' && !limitPx) block = 'Invalid Price';
  else if (!entry) block = 'No Quote';
  else if (margin === undefined) block = 'Invalid Margin';
  else if (margin === 0n) block = 'Enter Margin';
  else if (margin < globals.minMargin) block = `Min Margin $${fmtUsd(globals.minMargin, 0)}`;
  else if (margin > globals.maxMargin) block = `Max Margin $${fmtUsd(globals.maxMargin, 0)}`;
  else if (user && margin > user.balance) block = 'Insufficient Collateral';
  else if (notional > m.inst.maxNotional) block = 'Order Too Large';
  else if (lev > marketMaxLev) block = `${lev}× Coming Soon`;
  else {
    const size = (notional * BigInt(m.inst.priceScale)) / entry;
    const room = isLong ? m.caps.long - m.oi.long : m.caps.short - m.oi.short;
    if (size > (room > 0n ? room : 0n)) block = `${isLong ? 'Long' : 'Short'} OI Cap Reached`;
  }

  // delta neutral: a long and a short with the same margin and leverage, at market. The relayer batches the two,
  // but each leg can still fail on its own (price limit, cap, balance), so the outcome is reported per leg
  const longFill = fillPx(m, true);
  const shortFill = fillPx(m, false);
  let pairBlock: string | undefined;
  if (!m || !globals) pairBlock = 'Loading…';
  else if (kind === 'limit') pairBlock = 'Market Orders Only';
  else if (globals.openPaused || m.inst.paused || !m.inst.active) pairBlock = 'Market Paused';
  else if (!longFill || !shortFill) pairBlock = 'No Quote';
  else if (margin === undefined) pairBlock = 'Invalid Margin';
  else if (margin === 0n) pairBlock = 'Enter Margin';
  else if (margin < globals.minMargin) pairBlock = `Min Margin $${fmtUsd(globals.minMargin, 0)}`;
  else if (margin > globals.maxMargin) pairBlock = `Max Margin $${fmtUsd(globals.maxMargin, 0)}`;
  else if (user && margin * 2n > user.balance) pairBlock = `Needs $${fmtUsd(margin * 2n)}`;
  else if (notional > m.inst.maxNotional) pairBlock = 'Order Too Large';
  else if (lev > marketMaxLev) pairBlock = `${lev}× Coming Soon`;
  else {
    const roomL = m.caps.long - m.oi.long;
    const roomS = m.caps.short - m.oi.short;
    const sizeL = (notional * BigInt(m.inst.priceScale)) / longFill;
    const sizeS = (notional * BigInt(m.inst.priceScale)) / shortFill;
    if (sizeL > (roomL > 0n ? roomL : 0n) || sizeS > (roomS > 0n ? roomS : 0n)) pairBlock = 'OI Cap Reached';
  }

  const setMarginFrom = (v: bigint) => setMarginText(toInput(v, 18).replace(/(\.\d{2})\d+$/, '$1'));
  // 2× and Max stop at what can actually be posted: the balance, within the protocol's max margin
  const marginRoom = user && globals ? (user.balance < globals.maxMargin ? user.balance : globals.maxMargin) : undefined;
  const levPresets = [10, 25, 50, 100, 200, maxLev].filter((v, i, a) => v <= maxLev && a.indexOf(v) === i);
  const bustMove = bust && entry ? movePct(entry, bust) : undefined;

  /**
   * The signer for a trade: on the first one, offers one-click trading so the next ones skip the wallet; a refusal
   * holds for the visit (the Enable 1-Click chip stays)
   */
  async function tradingSigner(fallback: Signer): Promise<{ s: Signer; sessionExpiry?: number }> {
    if (session || sessionDeclined || !wallet) return { s: fallback, sessionExpiry: session?.expiry };
    setBusy('Enabling Trading…');
    try {
      const ns = await enableSession(wallet.address, wallet.client);
      setSession(ns);
      return { s: { kind: 'session', account: ns.account }, sessionExpiry: ns.expiry };
    } catch (e) {
      sessionDeclined = true;
      toast({ kind: 'info', title: 'One-click trading not enabled', body: `${errorMessage(e)}. Signing with the wallet.` });
      return { s: fallback };
    }
  }

  async function submitPair() {
    if (busy) return;
    if (!wallet) return openConnect(true);
    if (user && user.balance === 0n) return openFunds('deposit');
    if (pairBlock || !signer || !margin || !longFill || !shortFill) return;
    const leg = (long: boolean): Intent => ({
      action: 'open',
      request: {
        user: wallet.address,
        instrumentId: route.market,
        isLong: long,
        margin,
        leverage: lev,
        // each leg signs its own price limit, on its side of the band
        acceptablePx: priceLimit(long ? longFill : shortFill, long, slippage),
        nonce: newNonce(),
        deadline: deadlineIn(),
      },
    });
    const title = `${meta?.symbol} ${lev}× delta neutral`;
    try {
      const { s } = await tradingSigner(signer);
      setBusy('Placing Both Legs…');
      // signed one after the other (two prompts with the wallet), both handed over before either is awaited, so
      // the relayer can put them in the same batch
      const longId = await submitIntent(leg(true), s);
      let shortId: Hex | undefined;
      let shortError: unknown;
      try {
        shortId = await submitIntent(leg(false), s);
      } catch (e) {
        shortError = e;
      }
      setBusy('Filling Both Legs…');
      const [l, sh] = await Promise.all([settle(longId), shortId ? settle(shortId) : undefined]);
      const longOk = l.status === 'succeeded';
      const shortOk = sh?.status === 'succeeded';
      const why = (v: typeof l | undefined, e?: unknown) => (v ? (v.error ?? v.status) : errorMessage(e));
      if (longOk && shortOk) {
        toast({ kind: 'ok', title: `${title} opened`, body: `Long and short, $${fmtUsd(margin)} margin each. Net exposure: none.` });
      } else if (longOk || shortOk) {
        const open = longOk ? 'long' : 'short';
        toast({
          kind: 'err',
          title: `Only the ${open} leg opened`,
          body: `The ${longOk ? 'short' : 'long'} leg failed (${why(longOk ? sh : l, shortError)}). Your ${open} is a directional position: close it from Positions to stay flat.`,
        });
      } else {
        toast({ kind: 'err', title: `${title} failed`, body: `Neither leg opened: ${why(l)}.` });
      }
      refresh();
    } catch (e) {
      if (e instanceof UnsettledError) toast({ kind: 'info', title: 'Order status unknown', body: `${e.message} Check Positions.` });
      else toast({ kind: 'err', title: 'Order failed', body: errorMessage(e) });
      refresh();
    } finally {
      setBusy(undefined);
    }
  }

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (busy || (wallet && !(user && user.balance === 0n) && block)) return;
    if (!wallet) return openConnect(true);
    if (user && user.balance === 0n) return openFunds('deposit');
    if (!signer || margin === undefined) return;
    try {
      const { s, sessionExpiry } = await tradingSigner(signer);
      setBusy('Placing Order…');
      if (kind === 'limit') {
        const now = Math.floor(Date.now() / 1000);
        // a session key's signature dies with the key: the order cannot rest longer than the session
        const capped = s.kind === 'session' && sessionExpiry !== undefined && sessionExpiry - 60 < now + ttl;
        const deadline = capped ? sessionExpiry! - 60 : now + ttl;
        await submitIntent(
          {
            action: 'open',
            request: {
              user: wallet.address,
              instrumentId: route.market,
              isLong,
              margin,
              leverage: lev,
              // the limit is signed: the order can only open at this price or better
              acceptablePx: limitPx!,
              nonce: newNonce(),
              deadline: BigInt(deadline),
            },
          },
          s,
          'limit',
        );
        toast({
          kind: 'ok',
          title: `Limit ${isLong ? 'long' : 'short'} ${meta?.symbol} ${lev}× placed`,
          body: `Opens at $${fmtPx(limitPx!, m!.inst.priceScale)} or better${capped ? ', until your 1-click session ends' : ''}. See Orders.`,
        });
        refresh();
        return;
      }
      await run(
        {
          action: 'open',
          request: {
            user: wallet.address,
            instrumentId: route.market,
            isLong,
            margin,
            leverage: lev,
            // signed: the relayer cannot fill this order past the slippage the user accepted
            acceptablePx: priceLimit(entry!, isLong, slippage),
            nonce: newNonce(),
            deadline: deadlineIn(),
          },
        },
        s,
        (v) => {
          setBusy(v.status === 'pending' && v.ahead ? `In Queue · ${v.ahead} ahead…` : v.status === 'submitted' ? 'Filling…' : 'Placing Order…');
          setQueued(v.status === 'pending' && v.ahead ? { id: v.id, ahead: v.ahead } : undefined);
        },
      );
      toast({ kind: 'ok', title: `${isLong ? 'Long' : 'Short'} ${meta?.symbol} ${lev}× filled`, body: `$${fmtUsd(margin)} margin` });
      refresh();
    } catch (e) {
      if (e instanceof CancelledError) toast({ kind: 'info', title: 'Order cancelled', body: 'It left the queue unfilled.' });
      else if (e instanceof UnsettledError) toast({ kind: 'info', title: 'Order status unknown', body: e.message });
      else toast({ kind: 'err', title: 'Order failed', body: errorMessage(e) });
    } finally {
      setBusy(undefined);
      setQueued(undefined);
    }
  }

  const sliderPos = Math.round((Math.log(lev) / Math.log(maxLev)) * 1000);
  const label = !wallet
    ? 'Connect Wallet'
    : user && user.balance === 0n
      ? 'Deposit'
      : busy ?? block ?? `${kind === 'limit' ? 'Limit ' : ''}${isLong ? 'Long' : 'Short'} ${meta?.symbol}`;

  return (
    <form className="order" data-tour="order" onSubmit={submit}>
      <div className="seg order-kind" role="tablist" aria-label="Order type">
        {(['market', 'limit'] as const).map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={kind === k}
            className={kind === k ? 'is-on' : ''}
            onClick={() => {
              setKind(k);
              // start a limit from the current price, the user nudges it from there
              if (k === 'limit' && !pxText && fill) setPxText(toInput(fill, pxDecimals));
            }}
          >
            {k === 'market' ? 'Market' : 'Limit'}
          </button>
        ))}
      </div>
      <div className="side-switch" role="radiogroup" aria-label="Side">
        <button type="button" role="radio" aria-checked={isLong} className={isLong ? 'is-long' : ''} onClick={() => setLong(true)}>
          ↗ Long
        </button>
        <button type="button" role="radio" aria-checked={!isLong} className={!isLong ? 'is-short' : ''} onClick={() => setLong(false)}>
          ↘ Short
        </button>
      </div>
      {kind === 'limit' && (
        <>
          <label className="field-label" htmlFor="limit-px">
            <span className="avail">Now {fill && m ? `$${fmtPx(fill, m.inst.priceScale)}` : '- -'}</span>
            Limit Price (USD)
          </label>
          <div className="field">
            <button type="button" className="chip" disabled={!fill} title="Set to the current price" onClick={() => fill && setPxText(toInput(fill, pxDecimals))}>
              Now
            </button>
            <input
              id="limit-px"
              className="num"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0.00"
              value={pxText}
              onChange={(e) => setPxText(amountInput(e.target.value))}
            />
          </div>
        </>
      )}
      <label className="field-label" htmlFor="margin">
        <span className="avail">Avail. {user ? `$${fmtUsd(user.balance)}` : '- -'}</span>
        Margin (USDC)
      </label>
      <div className="field">
        <button type="button" className="chip" disabled={!margin} onClick={() => margin && setMarginFrom(margin / 2n)}>
          ½×
        </button>
        <button
          type="button"
          className="chip"
          disabled={!margin || !marginRoom}
          onClick={() => margin && marginRoom && setMarginFrom(margin * 2n > marginRoom ? marginRoom : margin * 2n)}
        >
          2×
        </button>
        <button
          type="button"
          className="chip"
          disabled={!marginRoom}
          title={globals ? `Up to the $${fmtUsd(globals.maxMargin, 0)} max margin` : undefined}
          onClick={() => marginRoom && setMarginFrom(marginRoom)}
        >
          Max
        </button>
        <input
          id="margin"
          className="num"
          inputMode="decimal"
          autoComplete="off"
          placeholder={globals ? `$${fmtUsd(globals.minMargin, 0)} – $${fmtUsd(globals.maxMargin, 0)}` : '0'}
          value={marginText}
          onChange={(e) => setMarginText(amountInput(e.target.value))}
        />
      </div>

      <label className="field-label" htmlFor="lev">
        Leverage
      </label>
      <div className="field">
        <div className="lev-presets" role="radiogroup" aria-label="Leverage presets">
          {levPresets.map((v) => (
            <button key={v} type="button" role="radio" aria-checked={lev === v} className={`chip ${lev === v ? 'is-on' : ''}`} onClick={() => setLevText(String(v))}>
              {v}×
            </button>
          ))}
        </div>
        <span className="input-suffix">
          <input
            id="lev"
            className="num"
            inputMode="numeric"
            autoComplete="off"
            value={levText}
            onChange={(e) => setLevText(e.target.value.replace(/\D/g, ''))}
            onBlur={() => setLevText(String(lev))}
          />
          <i>×</i>
        </span>
      </div>
      <input
        type="range"
        className="lev-slider"
        min={0}
        max={1000}
        value={sliderPos}
        onChange={(e) => setLevText(String(Math.max(1, Math.round(Math.exp((Number(e.target.value) / 1000) * Math.log(maxLev))))))}
        aria-label="Leverage"
      />
      <div className="slider-legend">
        <span>x1 Napping</span>
        <span>Zoomies x{maxLev}</span>
      </div>

      <QueueLine />
      <button
        type="submit"
        className={`btn btn-action ${isLong ? 'is-long' : 'is-short'}`}
        disabled={!!busy || (!!wallet && !(user && user.balance === 0n) && !!block)}
      >
        {label}
      </button>
      {queued && <QueuedNotice id={queued.id} ahead={queued.ahead} />}
      {kind === 'market' && (
        <div className="pair">
          <button
            type="button"
            className="btn btn-pair"
            disabled={!!busy || (!!wallet && !(user && user.balance === 0n) && !!pairBlock)}
            onClick={submitPair}
          >
            {busy ?? (wallet && !(user && user.balance === 0n) && pairBlock ? pairBlock : 'Delta Neutral · Long + Short')}
          </button>
        </div>
      )}

      <div className="order-facts">
        <Row label="Order Value">${fmtUsd(notional, notional >= 1000n * WAD ? 0 : 2)}</Row>
        <Row label="Bust Price">
          {bust && m ? (
            <>
              ${fmtPx(bust, m.inst.priceScale)}
              {bustMove !== undefined && <span className="muted"> ({bustMove > 0 ? '+' : '−'}{Math.abs(bustMove).toFixed(2)}%)</span>}
            </>
          ) : (
            '- -'
          )}
        </Row>
        {kind === 'limit' ? (
          <>
            <Row label="Opens">
              {fillsNow
                ? 'Now, at the current price'
                : limitMove !== undefined
                  ? `When the price ${isLong ? 'falls' : 'rises'} ${Math.abs(limitMove).toFixed(2)}%`
                  : '- -'}
            </Row>
            <div className="row">
              <span className="row-label">Expires In</span>
              <span className="slippage" role="radiogroup" aria-label="Expires in">
                {LIMIT_TTLS.map((t) => (
                  <button key={t.sec} type="button" role="radio" aria-checked={ttl === t.sec} className={ttl === t.sec ? 'is-on' : ''} onClick={() => setTtl(t.sec)}>
                    {t.label}
                  </button>
                ))}
              </span>
            </div>
            {session && session.expiry - 60 < Date.now() / 1000 + ttl && (
              <p className="order-note">Ends with your 1-click session, in {timeLeft(session.expiry).replace(' left', '')}.</p>
            )}
          </>
        ) : (
          <>
            <Row label="Fill Price">{entry && m ? `$${fmtPx(entry, m.inst.priceScale)}` : '- -'}</Row>
            <Row label={isLong ? 'Max Fill' : 'Min Fill'}>{entry && m ? `$${fmtPx(priceLimit(entry, isLong, slippage), m.inst.priceScale)}` : '- -'}</Row>
            <div className="row">
              <span className="row-label">Max Slippage</span>
              <span className="slippage" role="radiogroup" aria-label="Max slippage">
                {SLIPPAGE_CHOICES.map((b) => (
                  <button key={b} type="button" role="radio" aria-checked={slippage === b} className={slippage === b ? 'is-on' : ''} onClick={() => setSlippage(setSlippageBps(b))}>
                    {b / 100}%
                  </button>
                ))}
              </span>
            </div>
          </>
        )}
        <Row label={`Win at ${isLong ? '+' : '−'}1%`} tone={tone(winAt1)}>
          {winAt1 !== undefined ? fmtSignedUsd(winAt1) : '- -'}
        </Row>
        <Row label="PAPURR if Liquidated">{papurrIfLiquidated !== undefined ? fmtUnits(papurrIfLiquidated, 18, 0) : '- -'}</Row>
      </div>
    </form>
  );
}

/** "6d left", "5h left", "12m left" until a unix time */
function timeLeft(expiry: number): string {
  const s = Math.max(0, expiry - Date.now() / 1000);
  return s >= 86_400 ? `${Math.floor(s / 86_400)}d left` : s >= 3600 ? `${Math.floor(s / 3600)}h left` : `${Math.floor(s / 60)}m left`;
}

function SessionRow() {
  const { positions, wallet, session, setSession, toast } = useApp();
  const [busy, setBusy] = useState(false);
  return (
    <div className="session-row">
      <span>Open Positions {positions?.length ?? 0}</span>
      {wallet && (
        <button
          type="button"
          className={`chip ${session ? 'is-on' : ''}`}
          disabled={busy}
          title={
            session
              ? `One-click trading until ${new Date(session.expiry * 1000).toLocaleString()}. Click to renew it.`
              : 'Sign once, then trade without wallet prompts'
          }
          onClick={async () => {
            setBusy(true);
            try {
              setSession(await enableSession(wallet.address, wallet.client));
              toast({ kind: 'ok', title: 'One-click trading enabled' });
            } catch (e) {
              toast({ kind: 'err', title: 'Could not enable trading', body: errorMessage(e) });
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Signing…' : session ? `1-Click on · ${timeLeft(session.expiry)}` : 'Enable 1-Click'}
        </button>
      )}
    </div>
  );
}

/** Relayer backlog, shown only when there is one; warns when a new order could expire before it runs */
function QueueLine() {
  const { queue } = useApp();
  if (!queue || queue.pending + queue.submitted === 0) return null;
  const late = queue.etaSec > INTENT_TTL_SEC * 0.7;
  const level = queue.congestion;
  return (
    <div className={`queue-line${late ? ' is-late' : ''}`} role="status">
      <span className="dot" />
      {queue.pending + queue.submitted} order{queue.pending + queue.submitted > 1 ? 's' : ''} in queue · ~{queue.etaSec}s
      {level && (
        <span className={`congestion is-${level}`} title={`The queue needs ${queue.blocks ?? '?'} block(s) to clear`}>
          {level}
        </span>
      )}
      {late && <span> — busy network, an order may expire before it runs</span>}
      {queue.nextBlocks && queue.blockGas ? <BlockStrip blocks={queue.nextBlocks} budget={queue.blockGas} /> : null}
    </div>
  );
}

const mgas = (g: number) => `${(g / 1e6).toFixed(1)}M`;

/** The next blocks as gauges: gas already sent, then queued gas, against what one block carries */
function BlockStrip({ blocks, budget }: { blocks: { submitted: number; queued: number }[]; budget: number }) {
  return (
    <div className="block-strip">
      {blocks.map((b, i) => (
        <div
          key={i}
          className="block-gauge"
          title={`Block +${i + 1}: ${mgas(b.submitted)} sent · ${mgas(b.queued)} queued / ${mgas(budget)} gas`}
        >
          <span className="block-label">+{i + 1}</span>
          <span className="block-bar">
            <span className="is-sent" style={{ width: `${(100 * b.submitted) / budget}%` }} />
            <span className="is-queued" style={{ width: `${(100 * b.queued) / budget}%` }} />
          </span>
        </div>
      ))}
    </div>
  );
}

/** Under the order button while the order waits: why, roughly how long, and a way out */
function QueuedNotice({ id, ahead }: { id: Hex; ahead: number }) {
  const { queue, signer, toast } = useApp();
  const [cancelling, setCancelling] = useState(false);
  const wait = waitSec(queue, ahead);
  return (
    <div className={`queued-notice${wait > 10 ? ' is-slow' : ''}`} role="status">
      <span>
        {wait > 10 ? 'Your order may take a while' : 'Your order is queued'} · {ahead} ahead{wait > 0 ? ` · ~${wait}s` : ''}
        <span className="muted"> — larger orders and closes go first</span>
      </span>
      <button
        className="link"
        disabled={cancelling || !signer}
        onClick={async () => {
          setCancelling(true);
          try {
            await cancelIntent(id, signer!);
          } catch (e) {
            toast({ kind: 'err', title: 'Could not cancel', body: errorMessage(e) });
            setCancelling(false);
          }
        }}
      >
        {cancelling ? 'Cancelling…' : 'Cancel'}
      </button>
    </div>
  );
}
